import React, { useState } from 'react';
import { X, Check, Loader2, AlertTriangle } from 'lucide-react';

// ============================================================================
// PDP MODAL (Policy Decision Point Configuration)
// ============================================================================

const PDPModal = ({ config, data, onClose, onSave, saving, departmentSelected, departmentName }) => {
  const [formData, setFormData] = useState({
    vendor: data.vendor || '',
    product: data.product || '',
    integrationStatus: data.integrationStatus || 'not_configured',
    configNotes: data.configNotes || '',
    assessmentResponses: data.assessmentResponses || {},
    integrationChecks: data.integrationChecks || {},
    signalIntegrations: data.signalIntegrations || {},
    decisionTypes: data.decisionTypes || {},
    lastUpdated: data.lastUpdated || ''
  });

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVendorChange = (vendorName) => {
    setFormData(prev => ({ ...prev, vendor: vendorName, product: '' }));
  };

  // Get products for selected vendor
  const selectedVendorData = config.vendorOptions?.find(v => v.vendor === formData.vendor);
  const availableProducts = selectedVendorData?.products || [];

  const handleAssessmentChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      assessmentResponses: { ...prev.assessmentResponses, [questionId]: value }
    }));
  };

  const handleCheckChange = (checkItem) => {
    setFormData(prev => ({
      ...prev,
      integrationChecks: {
        ...prev.integrationChecks,
        [checkItem]: !prev.integrationChecks[checkItem]
      }
    }));
  };

  const handleSignalIntegrationChange = (signalId) => {
    setFormData(prev => ({
      ...prev,
      signalIntegrations: {
        ...prev.signalIntegrations,
        [signalId]: !prev.signalIntegrations[signalId]
      }
    }));
  };

  const handleDecisionTypeChange = (decisionId) => {
    setFormData(prev => ({
      ...prev,
      decisionTypes: {
        ...prev.decisionTypes,
        [decisionId]: !prev.decisionTypes[decisionId]
      }
    }));
  };

  const handleSave = () => {
    onSave({ ...formData, lastUpdated: new Date().toISOString() });
  };

  // Calculate readiness score
  const calculateReadinessScore = () => {
    const responses = formData.assessmentResponses;
    let score = 0;
    let total = 0;

    config.assessmentQuestions.forEach(q => {
      if (responses[q.id]) {
        total++;
        if (q.type === 'choice') {
          if (responses[q.id] === 'Yes') score += 100;
          else if (responses[q.id] === 'Partial') score += 50;
        } else if (q.type === 'percentage') {
          score += parseInt(responses[q.id]) || 0;
        }
      }
    });

    return total > 0 ? Math.round(score / total) : null;
  };

  // Calculate signal integration percentage
  const calculateSignalCoverage = () => {
    const integrated = Object.values(formData.signalIntegrations).filter(Boolean).length;
    return Math.round((integrated / config.signalIntegrations.length) * 100);
  };

  const readinessScore = calculateReadinessScore();
  const signalCoverage = calculateSignalCoverage();

  const integrationStatusOptions = [
    { value: 'not_configured', label: 'Not Configured', color: '#ef4444' },
    { value: 'partial', label: 'Partially Integrated', color: '#f59e0b' },
    { value: 'fully_integrated', label: 'Fully Integrated', color: '#10b981' }
  ];

  return (
    <div className="zta-modal-backdrop" onClick={onClose}>
      <div className="zta-modal zta-modal--pdp" onClick={e => e.stopPropagation()}>
        <div className="zta-modal-header">
          <div>
            <h2 className="zta-modal-title">{config.label}</h2>
            <p className="zta-modal-subtitle">
              {config.subtitle}
              {departmentName && ` · ${departmentName}`}
            </p>
          </div>
          <button className="zta-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="zta-modal-body zta-modal-body--scrollable">
          {/* Description */}
          <div className="zta-signal-description">
            <p>{config.description}</p>
          </div>

          {/* Score Badges */}
          <div className="zta-pdp-scores">
            {readinessScore !== null && (
              <div className={`zta-readiness-score ${readinessScore >= 75 ? 'zta-readiness-score--good' : readinessScore >= 50 ? 'zta-readiness-score--partial' : 'zta-readiness-score--low'}`}>
                <span className="zta-readiness-label">PDP Readiness</span>
                <span className="zta-readiness-value">{readinessScore}%</span>
              </div>
            )}
            <div className={`zta-readiness-score ${signalCoverage >= 80 ? 'zta-readiness-score--good' : signalCoverage >= 40 ? 'zta-readiness-score--partial' : 'zta-readiness-score--low'}`}>
              <span className="zta-readiness-label">Signal Coverage</span>
              <span className="zta-readiness-value">{signalCoverage}%</span>
            </div>
          </div>

          {/* Configuration Section */}
          {departmentSelected && (
            <>
              <div className="zta-signal-section">
                <h3>PDP Solution</h3>

                {/* Vendor Selection */}
                <div className="zta-modal-field">
                  <label>PDP Vendor</label>
                  <select
                    value={formData.vendor}
                    onChange={e => handleVendorChange(e.target.value)}
                    className="zta-modal-select"
                  >
                    <option value="">Select a vendor...</option>
                    {config.vendorOptions?.map(v => (
                      <option key={v.vendor} value={v.vendor}>{v.vendor}</option>
                    ))}
                  </select>
                </div>

                {/* Product Selection */}
                {formData.vendor && (
                  <div className="zta-modal-field">
                    <label>Product</label>
                    <select
                      value={formData.product}
                      onChange={e => handleFieldChange('product', e.target.value)}
                      className="zta-modal-select"
                    >
                      <option value="">Select a product...</option>
                      {availableProducts.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Integration Status */}
                <div className="zta-modal-field">
                  <label>Integration Status</label>
                  <div className="zta-status-buttons">
                    {integrationStatusOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`zta-status-btn ${formData.integrationStatus === opt.value ? 'active' : ''}`}
                        onClick={() => handleFieldChange('integrationStatus', opt.value)}
                        style={{ '--status-color': opt.color }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Signal Integrations */}
              <div className="zta-signal-section">
                <h3>Signal Sources Feeding PDP</h3>
                <p className="zta-section-hint">Which signals are connected to your Policy Decision Point?</p>
                <div className="zta-checklist zta-checklist--signals">
                  {config.signalIntegrations.map(signal => (
                    <label key={signal.id} className="zta-check-item zta-check-item--signal">
                      <input
                        type="checkbox"
                        checked={formData.signalIntegrations[signal.id] || false}
                        onChange={() => handleSignalIntegrationChange(signal.id)}
                      />
                      <div className="zta-check-content">
                        <span className="zta-check-label">{signal.label}</span>
                        <span className="zta-check-desc">{signal.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Decision Types */}
              <div className="zta-signal-section">
                <h3>Supported Decision Types</h3>
                <p className="zta-section-hint">What actions can your PDP take?</p>
                <div className="zta-checklist">
                  {config.decisionTypes.map(decision => (
                    <label key={decision.id} className="zta-check-item">
                      <input
                        type="checkbox"
                        checked={formData.decisionTypes[decision.id] || false}
                        onChange={() => handleDecisionTypeChange(decision.id)}
                      />
                      <div className="zta-check-content">
                        <span className="zta-check-label">{decision.label}</span>
                        <span className="zta-check-desc">{decision.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Integration Checklist */}
              <div className="zta-signal-section">
                <h3>Integration Checklist</h3>
                <div className="zta-checklist">
                  {config.integrationChecks.map((check, i) => (
                    <label key={i} className="zta-check-item">
                      <input
                        type="checkbox"
                        checked={formData.integrationChecks[check] || false}
                        onChange={() => handleCheckChange(check)}
                      />
                      <span>{check}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Assessment Section */}
              <div className="zta-signal-section">
                <h3>Readiness Assessment</h3>
                {config.assessmentQuestions.map(q => (
                  <div key={q.id} className="zta-modal-field">
                    <label>{q.question}</label>
                    {q.type === 'choice' && (
                      <div className="zta-choice-buttons">
                        {q.options.map(opt => (
                          <button
                            key={opt}
                            className={`zta-choice-btn ${formData.assessmentResponses[q.id] === opt ? 'active' : ''}`}
                            onClick={() => handleAssessmentChange(q.id, opt)}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.type === 'percentage' && (
                      <div className="zta-percentage-input">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={formData.assessmentResponses[q.id] || 0}
                          onChange={e => handleAssessmentChange(q.id, e.target.value)}
                          className="zta-range-input"
                        />
                        <span className="zta-percentage-value">
                          {formData.assessmentResponses[q.id] || 0}%
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="zta-modal-field">
                <label>Configuration Notes</label>
                <textarea
                  value={formData.configNotes}
                  onChange={e => handleFieldChange('configNotes', e.target.value)}
                  placeholder="Integration details, policy notes, contacts..."
                  className="zta-modal-textarea"
                  rows={3}
                />
              </div>
            </>
          )}

          {!departmentSelected && (
            <div className="zta-gov-no-dept">
              <AlertTriangle size={16} />
              Select a department to configure the Policy Decision Point
            </div>
          )}
        </div>

        <div className="zta-modal-footer">
          <button className="zta-modal-btn zta-modal-btn--secondary" onClick={onClose}>
            Close
          </button>
          {departmentSelected && (
            <button
              className="zta-modal-btn zta-modal-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
              Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDPModal;
